import { useState, useEffect } from "react";
import * as ApiService from "../services/apiService.js";
import * as DateUtils from "../utils/dateUtils.js";
import {
  createTimeEntryWithTracking,
  deleteTimeEntryWithTracking,
} from "../services/timeEntryService.js";
import { getLocalTimekeepingEntries } from "../services/azureTimekeepingService.js";
import { deriveEntryFields } from "../utils/timeEntryFields.js";
import { addRecentProject } from "../utils/recentProjects.js";

export const useTimeTracking = (
  selectedEmployee,
  currentWeek,
  contracts,
  activityTypes
) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // silent=true unterdrueckt das Lade-Overlay im Kalender (siehe Calendar.jsx
  // `loading`-Prop) - genutzt fuer den Hintergrund-Abgleich nach einer
  // optimistisch bereits angezeigten Verschiebung/Groessenaenderung.
  const loadTimeEntries = async ({ silent = false } = {}) => {
    if (!selectedEmployee) {
      setTimeEntries([]);
      return;
    }

    try {
      if (!silent) setLoading(true);
      const startDate = DateUtils.getWeekStart(currentWeek)
        .toISOString()
        .split("T")[0];
      const endDate = DateUtils.getWeekEnd(currentWeek)
        .toISOString()
        .split("T")[0];

      const [abaNinjaEntries, localEntries] = await Promise.all([
        ApiService.getTimeEntries(selectedEmployee.uuid, startDate, endDate),
        getLocalTimekeepingEntries(selectedEmployee.uuid, startDate, endDate),
      ]);

      setTimeEntries([...abaNinjaEntries, ...localEntries]);
      setError(null);
    } catch (err) {
      console.error("Fehler beim Laden der Zeiteinträge:", err);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const createTimeEntry = async (formData) => {
    if (!selectedEmployee) {
      throw new Error("Kein Mitarbeiter ausgewählt");
    }

    setLoading(true);
    setError(null);

    try {
      const timeBlocks = formData.timeBlocks || [];
      const activityType = activityTypes.find(
        (at) => at.uuid === formData.activityTypeUuid
      );
      const activityTypeName =
        activityType?.translations?.de?.description || "";

      // All time entries are now stored locally (no AbaNinja booking)
      await createTimeEntryWithTracking(
        selectedEmployee,
        formData,
        timeBlocks,
        activityTypeName,
        contracts,
        true
      );

      if (formData.localProjectCode) {
        addRecentProject(selectedEmployee.uuid, {
          code: formData.localProjectCode,
          name: formData.localProjectName,
        });
      }

      await loadTimeEntries();
      return { success: true };
    } catch (err) {
      setError("Fehler beim Erstellen des Zeiteintrags: " + err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTimeEntry = async (oldEntry, newFormData, options = {}) => {
    const { silent = false } = options;
    if (!selectedEmployee) {
      throw new Error("Kein Mitarbeiter ausgewählt");
    }
    if (!silent) setLoading(true);
    setError(null);
    try {
      // Alten Eintrag (bzw. bei Ganztag die ganze Gruppe) loeschen,
      // dann neuen Eintrag erstellen — atomar aus Nutzersicht,
      // Reload passiert erst am Ende.
      const isLocalOnly = oldEntry.source === "local";
      await deleteTimeEntryWithTracking(
        selectedEmployee.uuid,
        oldEntry.uuid,
        oldEntry,
        isLocalOnly
      );

      const timeBlocks = newFormData.timeBlocks || [];
      const activityType = activityTypes.find(
        (at) => at.uuid === newFormData.activityTypeUuid
      );
      const activityTypeName =
        activityType?.translations?.de?.description || newFormData.typeName || "";

      await createTimeEntryWithTracking(
        selectedEmployee,
        newFormData,
        timeBlocks,
        activityTypeName,
        contracts,
        true
      );

      await loadTimeEntries({ silent });
      return { success: true };
    } catch (err) {
      setError("Fehler beim Aktualisieren des Zeiteintrags: " + err.message);
      throw err;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Verschieben/Groesse aendern per Drag im Kalender: Projekt/Aktivitaet/
  // Beschreibung bleiben unveraendert, nur Datum/Start/Ende werden ersetzt.
  // Optimistisch: die Aenderung erscheint sofort (kein Lade-Overlay), der
  // eigentliche Loeschen+Neuanlegen-Zyklus in Azure laeuft im Hintergrund;
  // schlaegt er fehl, wird die optimistische Anzeige zurueckgerollt.
  const adjustTimeEntry = async (entry, { date, startTime, endTime }) => {
    const fields = deriveEntryFields(entry, activityTypes);
    const optimisticEntry = { ...entry, date, startTime, endTime };

    setTimeEntries((prev) =>
      prev.map((e) => (e === entry ? optimisticEntry : e))
    );

    try {
      return await updateTimeEntry(
        entry,
        {
          ...fields,
          localOnly: true,
          isFullDay: false,
          timeBlocks: [{ date, startTime, endTime, isFullDay: false }],
        },
        { silent: true }
      );
    } catch (err) {
      setTimeEntries((prev) =>
        prev.map((e) => (e === optimisticEntry ? entry : e))
      );
      // Loeschen+Neuanlegen ist nicht atomar - falls das Loeschen bereits
      // durchging und nur das Neuanlegen fehlschlug, waere die optimistische
      // Rueckrollung (zeigt den alten Eintrag) falsch. Stiller Hintergrund-
      // Abgleich holt den tatsaechlichen Azure-Stand nach.
      loadTimeEntries({ silent: true }).catch(() => {});
      throw err;
    }
  };

  const deleteTimeEntry = async (timeEntry) => {
    if (!selectedEmployee) {
      throw new Error("Kein Mitarbeiter ausgewählt");
    }

    setLoading(true);
    setError(null);

    try {
      const isLocalOnly = timeEntry.source === "local";

      await deleteTimeEntryWithTracking(
        selectedEmployee.uuid,
        timeEntry.uuid,
        timeEntry,
        isLocalOnly
      );
      await loadTimeEntries();
      return { success: true };
    } catch (err) {
      setError("Fehler beim Löschen des Zeiteintrags: " + err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeEntries();
  }, [selectedEmployee, currentWeek]);

  return {
    timeEntries,
    loading,
    error,
    createTimeEntry,
    updateTimeEntry,
    adjustTimeEntry,
    deleteTimeEntry,
    reloadTimeEntries: loadTimeEntries,
  };
};
